import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { PatientVital } from '@/types/vitals-api';

/**
 * POST /api/Patients/CreateVitals
 *
 * Used when this encounter has never recorded vitals before (i.e.
 * `useEncounterVitals` returned `[]`). The server creates one row per
 * vital code in the request — including null-valued ones for the
 * catalog template — and returns the created list with new identifiers.
 *
 * Wire shape verified by dev curl 2026-05-15:
 *   - Body is a BARE ARRAY of `PatientVital` rows (no envelope).
 *   - Each row has `identifire: null` on create.
 *   - `vitalNormalStatus` / `vitalAbnormalPercentage` ship as `null`
 *     on create — the server's vitals normalization service computes
 *     them after persistence. **Per the PROD-795 PR 1 Samer review §5
 *     commitment, React never ships pre-computed abnormal flags;
 *     the server is the sole authority on threshold evaluation.**
 *   - Numeric values ship as strings (`"37"`, not `37`).
 *   - `effectiveDateTime` + `createdDate` are no-Z ISO datetimes.
 *
 * On success, invalidates `['encounter-vitals', encounterId, patientId]`
 * so the read-side query refetches and renders the server's normalized
 * data (with `vitalNormalStatus` set).
 *
 * @see {@link ./use-update-vitals.ts} — PUT variant for subsequent saves
 */

interface CreateVitalsVariables {
  patientId: string;
  encounterId: string;
  vitals: PatientVital[];
}

export function useCreateVitals() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, CreateVitalsVariables>({
    mutationFn: ({ vitals }) =>
      post<unknown>('/api/Patients/CreateVitals', vitals),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['encounter-vitals', vars.encounterId, vars.patientId],
      });
    },
  });
}
